// frontend/src/components/SystemStatusWidget.js
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import '../styles/systemStatusWidget.css';

const SystemStatusWidget = () => {
  const [systemStatus, setSystemStatus] = useState({
    health: 'unknown',
    activeUsers: 0,
    deviceOnlineRate: 0,
    memoryUsage: 0,
    lastUpdate: null
  });
  const [loading, setLoading] = useState(true);

  // 解析 Prometheus 指标
  const parseMetrics = (text) => {
    const lines = text.split('\n').filter(line => line && !line.startsWith('#'));
    const metrics = {};
    
    lines.forEach(line => {
      const match = line.match(/^([a-zA-Z_:][a-zA-Z0-9_:]*){?([^}]*)}?\s+([\d.e-]+)/);
      if (match) {
        const [, name, labels, value] = match;
        if (!metrics[name]) metrics[name] = [];
        metrics[name].push({ value: parseFloat(value) });
      }
    });
    
    return metrics;
  };

  // 获取系统状态
  const fetchSystemStatus = async () => {
    try {
      // 并行获取健康状态和指标
      const [healthResponse, metricsResponse] = await Promise.all([
        fetch('/health'),
        fetch('/metrics')
      ]);

      const health = await healthResponse.json();
      const metricsText = await metricsResponse.text();
      const metrics = parseMetrics(metricsText);

      setSystemStatus({
        health: health.status || 'unknown',
        activeUsers: metrics.active_users_gauge?.[0]?.value || 0,
        deviceOnlineRate: (metrics.device_online_rate_gauge?.[0]?.value || 0) * 100,
        memoryUsage: (metrics.system_memory_usage_ratio?.[0]?.value || 0) * 100,
        lastUpdate: new Date()
      });
      
      setLoading(false);
    } catch (error) {
      console.error('获取系统状态失败:', error);
      setSystemStatus(prev => ({
        ...prev,
        health: 'error',
        lastUpdate: new Date()
      }));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 10000); // 每10秒更新
    return () => clearInterval(interval);
  }, []);

  const getHealthBadge = () => {
    switch (systemStatus.health) {
      case 'healthy':
        return <Badge bg="success">正常</Badge>;
      case 'error':
        return <Badge bg="danger">异常</Badge>;
      default:
        return <Badge bg="secondary">未知</Badge>;
    }
  };

  const getStatusColor = (value, thresholds = { good: 80, warning: 90 }) => {
    if (value < thresholds.good) return 'success';
    if (value < thresholds.warning) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <Card className="system-status-widget">
        <Card.Header>
          <h5>系统状态概览</h5>
        </Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">加载中...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="system-status-widget">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">系统状态概览</h5>
        <div className="d-flex align-items-center">
          {getHealthBadge()}
          <small className="text-muted ms-2">
            {systemStatus.lastUpdate && 
              `更新: ${systemStatus.lastUpdate.toLocaleTimeString()}`
            }
          </small>
        </div>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={3} sm={6} className="mb-3">
            <div className="status-metric">
              <div className="metric-icon">👥</div>
              <div className="metric-info">
                <div className="metric-value">{systemStatus.activeUsers}</div>
                <div className="metric-label">活跃用户</div>
              </div>
            </div>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <div className="status-metric">
              <div className="metric-icon">🎮</div>
              <div className="metric-info">
                <div className="metric-value">
                  {systemStatus.deviceOnlineRate.toFixed(1)}%
                </div>
                <div className="metric-label">设备在线率</div>
                <Badge 
                  bg={getStatusColor(systemStatus.deviceOnlineRate, { good: 90, warning: 95 })}
                  className="metric-badge"
                >
                  {systemStatus.deviceOnlineRate >= 95 ? '优秀' : 
                   systemStatus.deviceOnlineRate >= 90 ? '良好' : '需关注'}
                </Badge>
              </div>
            </div>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <div className="status-metric">
              <div className="metric-icon">💾</div>
              <div className="metric-info">
                <div className="metric-value">
                  {systemStatus.memoryUsage.toFixed(1)}%
                </div>
                <div className="metric-label">内存使用率</div>
                <Badge 
                  bg={getStatusColor(systemStatus.memoryUsage)}
                  className="metric-badge"
                >
                  {systemStatus.memoryUsage < 80 ? '正常' : 
                   systemStatus.memoryUsage < 90 ? '警告' : '高'}
                </Badge>
              </div>
            </div>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <div className="status-metric">
              <div className="metric-icon">⚡</div>
              <div className="metric-info">
                <div className="metric-value">
                  {systemStatus.health === 'healthy' ? '正常' : '异常'}
                </div>
                <div className="metric-label">系统健康</div>
                <Badge 
                  bg={systemStatus.health === 'healthy' ? 'success' : 'danger'}
                  className="metric-badge"
                >
                  {systemStatus.health === 'healthy' ? '运行中' : '需检查'}
                </Badge>
              </div>
            </div>
          </Col>
        </Row>
        
        <div className="text-center mt-3">
          <small className="text-muted">
            💡 点击 "系统监控" 菜单查看详细指标和图表
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SystemStatusWidget;
